import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRevalidator } from 'react-router';
import type {
  MeResponse,
  NotificationItem,
  NotificationPage,
  StreamClosedEvent,
  StreamNotificationEvent,
  UnreadCount,
} from '@hefesto/contracts';
import { get, post } from './api';

/**
 * Centro de notificaciones del navegador (01C). Fuentes = bandejas a las que el usuario tiene
 * acceso: la del equipo de Mot's (miembro interno activo) y la de cada empresa con
 * `notifications.view`. Cada fuente abre un `EventSource` (cookie de sesión, mismo origen) que solo
 * trae SEÑALES; el contenido siempre se pide por REST. Si el stream no está disponible, se consulta
 * el contador cada 60 s. Nada se guarda en localStorage.
 */
export interface NotificationSource {
  key: string;
  label: string;
  /** Prefijo REST: `/v1/platform` o `/v1/tenants/<id>`. */
  base: string;
}

export function sourcesFor(me: MeResponse, preferTenantId?: string | null): NotificationSource[] {
  const out: NotificationSource[] = [];
  if (me.platform?.status === 'active')
    out.push({ key: 'platform', label: "Mot's Digital", base: '/v1/platform' });
  for (const m of me.memberships)
    if (m.status === 'active' && m.permissions.includes('notifications.view'))
      out.push({
        key: `t:${m.tenant.id}`,
        label: m.tenant.name,
        base: `/v1/tenants/${m.tenant.id}`,
      });
  // Pocas conexiones en vivo por pestaña. (03A) La empresa en curso siempre entra, sin cambiar el
  // orden del resto (mismo orden = mismos streams al entrar y salir del portal).
  const kept = out.slice(0, MAX_SOURCES);
  const preferred = preferTenantId ? out.find((s) => s.key === `t:${preferTenantId}`) : undefined;
  if (preferred && !kept.includes(preferred)) kept[kept.length - 1] = preferred;
  return kept;
}

const MAX_SOURCES = 4;

export type Live = 'connecting' | 'live' | 'offline';

interface SourceState {
  unread: number;
  live: Live;
}

const POLL_MS = 60_000;

export interface NotificationCenter {
  sources: NotificationSource[];
  byKey: Record<string, SourceState>;
  totalUnread: number;
  /** Se incrementa con cada aviso nuevo (el panel abierto recarga su lista). */
  version: number;
  /**
   * Último aviso recibido en vivo, para anunciarlo a lectores de pantalla y para que cada área
   * invalide lo que corresponde según su `type` (02A: `core.tenant.created` → lista de clientes).
   */
  lastArrival: { source: string; at: number; type: string | null } | null;
  /** (03A) Bandeja que el panel abre primero: la de la empresa en curso, si hay una. */
  preferredKey: string | null;
  refresh(key?: string): void;
}

/** El centro del shell, compartido con las áreas (p. ej. `/admin` invalida su caché con él). */
export const NotificationCenterContext = createContext<NotificationCenter | null>(null);
export function useNotificationSignals(): NotificationCenter['lastArrival'] {
  return useContext(NotificationCenterContext)?.lastArrival ?? null;
}

export function useNotificationCenter(
  me: MeResponse,
  currentTenantId?: string | null,
): NotificationCenter {
  // Misma lista de fuentes = mismos streams (revalidar la sesión no reabre conexiones).
  const computed = sourcesFor(me, currentTenantId);
  const signature = computed.map((s) => `${s.key}=${s.label}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sources = useMemo(() => computed, [signature]);
  const [byKey, setByKey] = useState<Record<string, SourceState>>({});
  const [version, setVersion] = useState(0);
  const [lastArrival, setLastArrival] = useState<NotificationCenter['lastArrival']>(null);
  const revalidator = useRevalidator();
  const revalidate = useRef(revalidator.revalidate);
  useEffect(() => {
    revalidate.current = revalidator.revalidate;
  }, [revalidator.revalidate]);

  const loadCount = useCallback(async (s: NotificationSource) => {
    try {
      const { unread } = await get<UnreadCount>(`${s.base}/notifications/unread-count`);
      setByKey((prev) => ({
        ...prev,
        [s.key]: { live: prev[s.key]?.live ?? 'connecting', unread },
      }));
    } catch {
      /* se reintenta en la próxima señal o en el sondeo */
    }
  }, []);

  useEffect(() => {
    const cleanups: (() => void)[] = [];
    for (const s of sources) {
      // El contador se carga al abrir el stream (`ready`) o, si no hay stream, al caer al sondeo.
      const setLive = (live: Live) =>
        setByKey((prev) => ({ ...prev, [s.key]: { unread: prev[s.key]?.unread ?? 0, live } }));
      let poll: number | undefined;
      const startPolling = () => {
        if (poll === undefined) poll = window.setInterval(() => void loadCount(s), POLL_MS);
      };
      const es = new EventSource(`/api${s.base}/stream`);
      es.addEventListener('ready', () => {
        setLive('live');
        window.clearInterval(poll);
        poll = undefined;
        void loadCount(s);
      });
      es.addEventListener('notification', (ev) => {
        void loadCount(s);
        setVersion((v) => v + 1);
        let type: string | null = null;
        try {
          type =
            (JSON.parse((ev as MessageEvent<string>).data) as StreamNotificationEvent).type ?? null;
        } catch {
          /* señal sin tipo: igual se recarga */
        }
        setLastArrival({ source: s.key, at: Date.now(), type });
      });
      es.addEventListener('reset', () => {
        void loadCount(s);
        setVersion((v) => v + 1);
      });
      es.addEventListener('closed', (ev) => {
        const { reason } = JSON.parse((ev as MessageEvent<string>).data) as StreamClosedEvent;
        if (reason === 'revoked') {
          // Sesión o acceso retirados: no reconectar; recargar la sesión (puede llevar al login).
          es.close();
          setLive('offline');
          revalidate.current();
        }
        // (03A) Cambiaron TUS roles o tu estado en esa empresa: recargar permisos y reconectar
        // (el servidor re-autoriza; si ya no sos parte, el stream no vuelve).
        if (reason === 'access_changed') revalidate.current();
        // entitlements_changed / shutdown: EventSource reconecta solo y se re-autoriza.
      });
      es.onerror = () => {
        if (es.readyState === EventSource.CLOSED) {
          setLive('offline');
          void loadCount(s);
          startPolling();
        } else setLive('connecting');
      };
      cleanups.push(() => {
        es.close();
        window.clearInterval(poll);
      });
    }
    return () => cleanups.forEach((c) => c());
  }, [sources, loadCount]);

  const totalUnread = sources.reduce((n, s) => n + (byKey[s.key]?.unread ?? 0), 0);
  const refresh = useCallback(
    (key?: string) => {
      for (const s of sources) if (!key || s.key === key) void loadCount(s);
    },
    [sources, loadCount],
  );
  const preferredKey =
    currentTenantId && sources.some((s) => s.key === `t:${currentTenantId}`)
      ? `t:${currentTenantId}`
      : null;
  return { sources, byKey, totalUnread, version, lastArrival, preferredKey, refresh };
}

export const fetchNotifications = (s: NotificationSource, cursor?: string | null) =>
  get<NotificationPage>(
    `${s.base}/notifications?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
  );

export const markRead = (s: NotificationSource, body: { ids: string[] } | { all: true }) =>
  post<{ updated: number }>(`${s.base}/notifications/read`, body);

const rtf = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });

/** "hace 5 minutos", "ayer"… */
export function relativeTime(iso: string, now = Date.now()): string {
  const diff = (new Date(iso).getTime() - now) / 1000;
  const abs = Math.abs(diff);
  if (abs < 45) return 'recién';
  if (abs < 3_600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diff / 3_600), 'hour');
  if (abs < 604_800) return rtf.format(Math.round(diff / 86_400), 'day');
  return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

export type { NotificationItem };
